import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTournaments } from '../../../shared/hooks/useTournaments';
import { useSeasons } from '../../../shared/hooks/useSeasons';
import { useAllStandingsConfigs } from '../../../shared/hooks/useMatchPoints';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';
import { supabase } from '../../../shared/lib/supabase';

interface ReportsTournamentDetailPageProps {
  isMobile?: boolean;
  onToggleSidebar?: () => void;
}

// Generate dynamic tie-breaker rules based on config
const generateTieBreakerRules = (config: any) => {
  const pointsText = `Points: Win (${config.default_win_points}), Draw (${config.default_draw_points}), Loss (${config.default_loss_points})`;

  let tieBreakerOrder: string[] = [];
  if (config.tie_breaker_order) {
    if (typeof config.tie_breaker_order === 'string') {
      try {
        tieBreakerOrder = JSON.parse(config.tie_breaker_order);
      } catch (e) {
        tieBreakerOrder = [];
      }
    } else if (Array.isArray(config.tie_breaker_order)) {
      tieBreakerOrder = config.tie_breaker_order;
    }
  }

  const criteriaMap: { [key: string]: string } = {
    'goal_difference': 'goal difference',
    'goals_for': 'goals for',
    'goals_against': 'goals against',
    'head_to_head': 'head to head result',
  };

  const tieBreakerDisplay = tieBreakerOrder.length > 0 
    ? tieBreakerOrder.map(c => criteriaMap[c] || c).join(', ') 
    : 'none';

  return {
    pointsText: pointsText,
    tieBreakerText: `Tie-breaker order: ${tieBreakerDisplay}`
  };
};

// League Standings Display Component - Exact copy from Score App
const LeagueStandingsDisplay: React.FC<{ tournamentId: string; seasonId?: string; config: any }> = ({ 
  tournamentId, 
  seasonId, 
  config
}) => {
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTeamStandings = async () => {
      if (!config.id) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true)
          .order('position', { ascending: true });
        
        if (error) {
          console.error('Error fetching team standings:', error);
          return;
        }
        
        setTeamStandingsData(data || []);
      } catch (error) {
        console.error('Error fetching team standings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamStandings();
  }, [config.id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading league standings...</p>
        </div>
      </div>
    );
  }

  if (teamStandingsData.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">📊</div>
          <p className="text-gray-600">No standings data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Publish team standings from the Points tab to view data here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h6 className="text-sm font-semibold text-gray-900">League Standings</h6>
          <p className="text-xs text-gray-600 mt-1">Final standings from team_standings table</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">GD</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {teamStandingsData.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="py-3 px-3">
                    <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                      team.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                      team.position === 2 ? 'bg-gray-100 text-gray-800' :
                      team.position === 3 ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {team.position}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    {team.teams?.name || 'Unknown Team'}
                  </td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                  <td className="py-3 px-4 text-center text-gray-600">{team.goal_difference}</td>
                  <td className="py-3 px-4 text-center font-medium text-gray-900">{team.total_points}</td>
                  <td className="py-3 px-2 text-center">
                    {team.is_qualified ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-green-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {team.is_winner ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-yellow-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {team.is_knocked_out ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-red-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Knockout Standings Display Component - Exact copy from Score App
const KnockoutStandingsDisplay: React.FC<{ tournamentId: string; seasonId?: string; config: any }> = ({ 
  tournamentId, 
  seasonId, 
  config
}) => {
  const [knockoutMatchesData, setKnockoutMatchesData] = useState<any[]>([]);
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!config.id) return;
      
      try {
        setLoading(true);
        
        // Fetch knockout matches with proper joins
        const { data: knockoutMatches, error: knockoutError } = await supabase
          .from('knockout_matches')
          .select(`
            *,
            matches(
              id,
              name,
              home_team_id,
              away_team_id,
              start_date,
              home_score,
              away_score,
              home_penalty_score,
              away_penalty_score,
              home_teams:teams!home_team_id(
                id,
                name,
                short_name,
                logo
              ),
              away_teams:teams!away_team_id(
                id,
                name,
                short_name,
                logo
              )
            )
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true)
          .order('knockout_level', { ascending: true })
          .order('level_position', { ascending: true });
        
        if (knockoutError) {
          console.error('Error fetching knockout matches:', knockoutError);
          return;
        }
        
        setKnockoutMatchesData(knockoutMatches || []);
        
        // Fetch team standings
        const { data: teamData, error: teamError } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true)
          .order('position', { ascending: true });
        
        if (teamError) {
          console.error('Error fetching team standings:', teamError);
        } else {
          setTeamStandingsData(teamData || []);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [config.id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading knockout standings...</p>
        </div>
      </div>
    );
  }

  if (knockoutMatchesData.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">🏆</div>
          <p className="text-gray-600">No knockout matches available</p>
        </div>
      </div>
    );
  }

  // Group matches by knockout level and sort by level_position
  const matchesByLevel = knockoutMatchesData.reduce((acc, match) => {
    const level = match.knockout_level;
    if (!acc[level]) acc[level] = [];
    acc[level].push(match);
    return acc;
  }, {} as {[key: number]: any[]});

  // Sort matches within each level by level_position
  Object.keys(matchesByLevel).forEach(level => {
    matchesByLevel[parseInt(level)].sort((a: any, b: any) => a.level_position - b.level_position);
  });

  // Create bracket structure - smaller knockout_level means earlier round
  const sortedLevels = Object.keys(matchesByLevel)
    .map(level => parseInt(level))
    .sort((a, b) => a - b);

  return (
    <div className="p-6 space-y-6">
      {/* Knockout Bracket */}
      {Object.keys(matchesByLevel).length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900">Knockout Bracket</h6>
            <p className="text-xs text-gray-600 mt-1">Tournament progression from early rounds to finals</p>
          </div>
          <div className="p-6">
            {/* Create a proper bracket layout */}
            <div className="flex justify-center overflow-x-auto">
              <div className="flex space-x-12 min-w-max">
                {sortedLevels.map((level, levelIndex) => (
                  <div key={level} className="flex-shrink-0">
                    {/* Level Header - No static names */}
                    <div className="text-center mb-6">
                      <div className="w-40 h-0.5 bg-blue-300 mx-auto"></div>
                    </div>
                    
                    {/* Matches in this level */}
                    <div className="space-y-4">
                        {matchesByLevel[level].map((match: any, matchIndex: number) => {
                          return (
                          <div key={match.id} className="relative" style={{ marginTop: `${(level - 1) * 20}px` }}>
                            {/* Match Card */}
                            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border-2 border-blue-300 w-52 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-blue-400 relative overflow-hidden">
                              {/* Blue accent bar at top */}
                              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>
                              {/* Match name */}
                              {(match.matches?.name && String(match.matches.name).trim() !== '') && (
                                <div className="text-xs font-semibold text-gray-800 truncate mb-2" title={match.matches.name}>
                                  {match.matches.name}
                                </div>
                              )}
                              {/* Teams and Score */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                  <div className="flex items-center space-x-2 flex-1">
                                    {match.matches?.home_teams?.logo && (
                                      <img 
                                        src={match.matches.home_teams.logo} 
                                        alt={match.matches.home_teams.name}
                                        className="w-6 h-6 rounded-full object-cover"
                                      />
                                    )}
                                    <span className="font-medium text-gray-900 truncate">
                                      {match.matches?.home_teams?.short_name || match.matches?.home_teams?.name || `Team ${match.matches?.home_team_id || 'A'}`}
                                    </span>
                                  </div>
                                  <span className="font-bold text-gray-800 text-lg">
                                    {match.matches?.home_score ?? '?'}
                                    {match.matches?.home_penalty_score > 0 && (
                                      <span className="text-xs text-gray-600 ml-1">
                                        ({match.matches.home_penalty_score})
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                  <div className="flex items-center space-x-2 flex-1">
                                    {match.matches?.away_teams?.logo && (
                                      <img 
                                        src={match.matches.away_teams.logo} 
                                        alt={match.matches.away_teams.name}
                                        className="w-6 h-6 rounded-full object-cover"
                                      />
                                    )}
                                    <span className="font-medium text-gray-900 truncate">
                                      {match.matches?.away_teams?.short_name || match.matches?.away_teams?.name || `Team ${match.matches?.away_team_id || 'B'}`}
                                    </span>
                                  </div>
                                  <span className="font-bold text-gray-800 text-lg">
                                    {match.matches?.away_score ?? '?'}
                                    {match.matches?.away_penalty_score > 0 && (
                                      <span className="text-xs text-gray-600 ml-1">
                                        ({match.matches.away_penalty_score})
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Match Date */}
                              {match.matches?.start_date && (
                                <div className="text-center mt-3 pt-2 border-t border-blue-200">
                                  <span className="text-xs text-gray-600">
                                    {new Date(match.matches.start_date).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          
                          {/* Connection Lines to Next Level */}
                          {levelIndex < sortedLevels.length - 1 && (
                            <>
                              {/* Horizontal line */}
                              <div className="absolute top-1/2 -right-6 w-6 h-0.5 bg-gray-800 transform -translate-y-1/2"></div>
                              {/* Vertical line */}
                              <div className="absolute top-1/2 -right-6 w-0.5 h-8 bg-gray-800 transform -translate-y-1/2"></div>
                            </>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Status */}
      {teamStandingsData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900">Team Status</h6>
            <p className="text-xs text-gray-600 mt-1">Final team standings and status</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">GD</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamStandingsData.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                        team.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                        team.position === 2 ? 'bg-gray-100 text-gray-800' :
                        team.position === 3 ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {team.position}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">
                      {team.teams?.name || 'Unknown Team'}
                    </td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{team.goal_difference}</td>
                    <td className="py-3 px-2 text-center">
                      {team.is_qualified ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-green-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {team.is_winner ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-yellow-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {team.is_knocked_out ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-red-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Group Standings Display Component - Exact copy from Score App
const GroupStandingsDisplay: React.FC<{ tournamentId: string; seasonId?: string; config: any }> = ({ 
  tournamentId, 
  seasonId, 
  config
}) => {
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTeamStandings = async () => {
      if (!config.id) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true)
          .order('position', { ascending: true });
        
        if (error) {
          console.error('Error fetching team standings:', error);
          return;
        }
        
        setTeamStandingsData(data || []);
      } catch (error) {
        console.error('Error fetching team standings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamStandings();
  }, [config.id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading group standings...</p>
        </div>
      </div>
    );
  }

  if (teamStandingsData.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">📊</div>
          <p className="text-gray-600">No group standings data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h6 className="text-sm font-semibold text-gray-900">Group Stage Standings</h6>
          <p className="text-xs text-gray-600 mt-1">Final standings from team_standings table</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">GD</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {teamStandingsData.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="py-3 px-3">
                    <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                      team.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                      team.position === 2 ? 'bg-gray-100 text-gray-800' :
                      team.position === 3 ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {team.position}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    {team.teams?.name || 'Unknown Team'}
                  </td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                  <td className="py-3 px-4 text-center text-gray-600">{team.goal_difference}</td>
                  <td className="py-3 px-4 text-center font-medium text-gray-900">{team.total_points}</td>
                  <td className="py-3 px-2 text-center">
                    {team.is_qualified ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-green-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {team.is_winner ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-yellow-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {team.is_knocked_out ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-red-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-gray-300">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ReportsTournamentDetailPage: React.FC<ReportsTournamentDetailPageProps> = ({ isMobile = false, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>('all');

  // Get tournament data
  const { tournaments } = useTournaments();
  const tournament = tournaments.find(t => t.id === tournamentId);

  // Get seasons data
  const { seasons } = useSeasons();

  // Get tournament standings configurations
  const { configs: standingsConfigs } = useAllStandingsConfigs(tournamentId || '');

  // Handle season change
  const handleSeasonChange = (seasonId: string) => {
    setSelectedSeasonId(seasonId);
  };

  // Get available seasons for dropdown - only seasons used in active configs
  const getAvailableSeasons = () => {
    const seasonsList = [
      { id: 'all', name: 'All Seasons' }
    ];
    
    // Get unique season IDs from active configurations
    const activeConfigs = standingsConfigs.filter(config => config.is_active);
    const usedSeasonIds = new Set(activeConfigs.map(config => config.season_id).filter(Boolean));
    
    // Only add seasons that are actually used in configurations
    seasons.forEach(season => {
      if (usedSeasonIds.has(season.id)) {
        seasonsList.push({
          id: season.id,
          name: season.name
        });
      }
    });
    
    return seasonsList;
  };

  // Handle go back
  const handleGoBack = () => {
    const orgIdFromPath = extractOrgIdFromPath(window.location.pathname);
    const actualOrgId = orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}/reports/standings`);
  };

  // Filter active configurations based on selected season
  const activeConfigs = standingsConfigs.filter(config => {
    if (!config.is_active) return false;
    
    // If "All Seasons" is selected, show all active configs
    if (selectedSeasonId === 'all') return true;
    
    // Otherwise, filter by selected season
    return config.season_id === selectedSeasonId;
  });

  if (!tournament) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">❌</div>
          <p className="text-gray-600">Tournament not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Go Back and Season Dropdown */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Go Back Button */}
          <div className="flex items-center">
            <button
              onClick={handleGoBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Go Back</span>
            </button>
          </div>

          {/* Centered Tournament Card */}
          <div className="flex items-center justify-center flex-1">
            <div className="flex items-center space-x-3">
              {/* Tournament Logo */}
              <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                {tournament.logo ? (
                  <img 
                    src={tournament.logo} 
                    alt={tournament.name}
                    className="w-8 h-8 object-contain"
                  />
                ) : (
                  <span className="text-blue-600 text-lg font-bold">
                    {tournament.name.charAt(0)}
                  </span>
                )}
              </div>
              
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{tournament.name}</h1>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span>• {tournament.country || 'International'}</span>
                  <span>Tournament Details</span>
                </div>
              </div>
            </div>
          </div>

          {/* Season Dropdown */}
          <div className="flex items-center space-x-4">
            {getAvailableSeasons().length > 1 ? (
              <SearchableDropdown
                value={selectedSeasonId}
                onChange={handleSeasonChange}
                options={getAvailableSeasons()}
                placeholder="Select season"
                className="w-48"
              />
            ) : (
              <div className="w-48 px-3 py-2 text-sm text-gray-500 bg-gray-100 rounded-md border">
                No seasons configured
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Active Configurations */}
          {activeConfigs.length > 0 ? (
            <div className="space-y-6">
              {activeConfigs.map((config) => {
                const rules = generateTieBreakerRules(config);
                
                return (
                  <div key={config.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {/* Config Header */}
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h5 className="text-md font-semibold text-gray-900">
                            {config.stage_name || 'General Configuration'}
                          </h5>
                          <p className="text-sm text-gray-600 mt-1">
                            {config.season_id ? seasons.find(s => s.id === config.season_id)?.name : 'All Seasons'} • {config.format_type}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{rules.pointsText}</p>
                          <p className="text-xs text-gray-500">{rules.tieBreakerText}</p>
                        </div>
                      </div>
                    </div>

                    {/* Format-based rendering */}
                    {config.format_type === 'league' && tournamentId && (
                      <LeagueStandingsDisplay 
                        tournamentId={tournamentId} 
                        seasonId={selectedSeasonId === 'all' ? config.season_id : selectedSeasonId}
                        config={config}
                      />
                    )}

                    {config.format_type === 'knockout' && tournamentId && (
                      <KnockoutStandingsDisplay 
                        tournamentId={tournamentId} 
                        seasonId={selectedSeasonId === 'all' ? config.season_id : selectedSeasonId}
                        config={config}
                      />
                    )}

                    {(config.format_type === 'group_stage' || config.format_type === 'group') && tournamentId && (
                      <GroupStandingsDisplay 
                        tournamentId={tournamentId} 
                        seasonId={selectedSeasonId === 'all' ? config.season_id : selectedSeasonId}
                        config={config}
                      />
                    )}

                    {!config.format_type && (
                      <div className="p-6">
                        <div className="text-center py-8">
                          <div className="text-gray-400 text-4xl mb-3">📊</div>
                          <p className="text-gray-600">Standings will be displayed here</p>
                          <p className="text-sm text-gray-500 mt-1">
                            Format not specified in configuration
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🏆</div>
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Active Configurations</h2>
              <p className="text-gray-500">Create a tournament configuration to view standings.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsTournamentDetailPage;